import { Input, Modal, ModalProps, Typography } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import { JobPostingDetail, CandidateDetail } from '../types';
import Form from './Form';

interface ActivateApplicantFormValues {
  annotation: string;
}

interface ActivateApplicantModalProps extends ModalProps {
  candidateId?: CandidateDetail['id'];
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: ActivateApplicantFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const ActivateApplicantModal = ({
  jobPostingId,
  candidateId,
  onSuccess,
  okButtonProps,
  ...props
}: ActivateApplicantModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: candidateId,
          job_posting: jobPostingId,
        },
      },
    ],
    {
      select(data) {
        return data?.results?.[0];
      },
      enabled: !!candidateId && !!jobPostingId,
    }
  );

  const candidate = useAPIQuery(`/candidates/${candidateId}/`, {
    enabled: !!candidateId,
  });

  const mutation = useAPIMutation(
    async ({ annotation }: ActivateApplicantFormValues, api) => {
      const recruitmentProcesses = await api.get('/recruitment-processes/', {
        params: {
          candidate: candidateId,
          job_posting: jobPostingId,
        },
      });

      if (recruitmentProcesses.data?.results[0].status === 'INACTIVE') {
        await api.patch(`/recruitment-processes/${recruitmentProcesses.data?.results[0].id}/`, {
          status: 'ACTIVE',
          commentary: `${t('Justificativa')}: ` + annotation,
        });
      }
    },
    {
      onSuccess,
      onSettled: async (_, __) => {
        await queryClient.invalidateQueries('useSearchCandidates');
        await queryClient.invalidateQueries('/recruitment-processes/');
        await queryClient.invalidateQueries(
          `/recruitment-processes/${recruitmentProcess.data?.id}/`
        );
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  return (
    <Modal
      onOk={() => form.submit()}
      okButtonProps={{ loading: mutation.isLoading, ...okButtonProps }}
      title={
        <Typography.Text>
          <Trans>Retornar</Trans> <Typography.Text strong>{candidate.data?.name}</Typography.Text>
        </Typography.Text>
      }
      {...props}
    >
      <Helmet>
        <title>
          {t('Retornar')} {candidate.data?.name || '...'} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<ActivateApplicantFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          annotation: '',
        }}
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      >
        <Typography.Paragraph>
          <Trans>Ao confirmar,</Trans>{' '}
          <Typography.Text strong>{candidate.data?.name}</Typography.Text>{' '}
          <Trans>retornará ao processo seletivo.</Trans>
        </Typography.Paragraph>
        <Form.Item
          label={t('Justificativa')}
          name="annotation"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ActivateApplicantModal;
